import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faCheckCircle, faTimesCircle, faBook, faUser, 
  faGraduationCap, faChartLine, faIdCard, faEnvelope, faPhone, 
  faCalendar, faHashtag, faPrint
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import matrizCurricularService from '../../../services/matrizCurricularService';
import matriculaService from '../../../services/matriculaService';
import { useAuth } from '../../../contexts/AuthContext';

const AlunoProgressoView = () => {
  const navigate = useNavigate();
  const { idMatriz, idAluno } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [matriz, setMatriz] = useState(null);
  const [alunoInfo, setAlunoInfo] = useState(null);

  useEffect(() => {
    loadProgresso();
  }, [idMatriz, idAluno]);

  const handleImprimirPdf = async () => {
    try {
      setLoadingPdf(true);
      toast.info('Gerando PDF... Por favor, aguarde. Isso pode levar alguns segundos.');
      
      const searchParams = new URLSearchParams(window.location.search);
      const idCurso = searchParams.get('idCurso');
      const alunoId = idAluno || user?.idPessoa;
      
      const result = await matrizCurricularService.imprimirProgressoAluno(idMatriz, alunoId, idCurso);
      
      if (result.success) {
        toast.success('PDF gerado com sucesso! O download deve iniciar automaticamente.');
      } else {
        toast.error(result.error || 'Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      toast.error('Erro ao gerar PDF do progresso. Tente novamente.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const loadProgresso = async () => {
    try {
      setLoading(true);

      // Se idAluno não foi passado, usa o ID do usuário logado
      const alunoId = idAluno || user?.idPessoa;

      if (!alunoId) {
        toast.error('ID do aluno não identificado');
        return;
      }

      // Extrair idCurso e idMatricula da URL (query parameters)
      const searchParams = new URLSearchParams(window.location.search);
      const idCurso = searchParams.get('idCurso');
      const idMatricula = searchParams.get('idMatricula');

      if (!idCurso) {
        toast.error('ID do curso não identificado');
        return;
      }

      // Se idMatriz for 'auto', buscar matriz automaticamente
      let matrizId = idMatriz;
      
      if (idMatriz === 'auto') {
        if (!idMatricula) {
          toast.error('Informações insuficientes para buscar matriz curricular');
          return;
        }

        try {
          // Buscar matriz vinculada ou sugerida pela API
          const matrizResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/matriculas/${idMatricula}/matriz`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          const matrizData = await matrizResponse.json();

          if (matrizResponse.ok && matrizData.success) {
            matrizId = matrizData.data.matriz.id;
            
            // Mostrar mensagem se for matriz sugerida (não vinculada)
            if (matrizData.data.sugestao) {
              toast.info(matrizData.data.mensagem, { autoClose: 5000 });
            }
          } else {
            toast.error(matrizData.message || 'Nenhuma matriz curricular encontrada para este curso');
            return;
          }
        } catch (err) {
          console.error('Erro ao buscar matriz:', err);
          toast.error('Erro ao buscar matriz curricular. Por favor, configure uma matriz para este curso.');
          return;
        }
      }

      // Carregar progresso com a matriz identificada
      const response = await matrizCurricularService.getProgressoAluno(matrizId, alunoId, idCurso);
      
      if (response.success) {
        setProgresso(response.data);
      } else {
        toast.error(response.message || 'Erro ao carregar progresso');
      }

      // Carregar dados da matriz
      const matrizResponse = await matrizCurricularService.getByIdWithModulos(matrizId);
      if (matrizResponse.success) {
        setMatriz(matrizResponse.data);
      }

      // Buscar matrícula do aluno para obter informações completas
      try {
        const matriculaResponse = await matriculaService.buscarMatriculasAluno(alunoId);
        if (matriculaResponse.success && matriculaResponse.data.length > 0) {
          // Encontrar a matrícula do curso específico
          const matriculaAtual = matriculaResponse.data.find(m => 
            String(m.idCurso) === String(idCurso)
          );
          if (matriculaAtual) {
            setAlunoInfo(matriculaAtual);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar informações do aluno:', error);
      }
    } catch (err) {
      console.error('Erro ao carregar progresso:', err);
      toast.error(err.message || 'Erro ao carregar progresso do aluno');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Carregando progresso...</div>
      </div>
    );
  }

  if (!progresso || !matriz) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          {/* Header com botão voltar */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Progresso do Aluno</h1>
            </div>
          </div>

          {/* Card de mensagem amigável */}
          <div className="max-w-2xl mx-auto mt-12">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              {/* Ícone grande */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full">
                  <FontAwesomeIcon 
                    icon={faChartLine} 
                    className="text-4xl text-blue-600" 
                  />
                </div>
              </div>

              {/* Título */}
              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                Progresso Não Disponível
              </h2>

              {/* Mensagem explicativa */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                Não foi possível carregar o progresso deste aluno no momento. 
                Isso pode acontecer quando não há matriz curricular ativa configurada 
                para o curso na data da matrícula.
              </p>

              {/* Lista de possíveis causas */}
              <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faBook} className="text-blue-600" />
                  Possíveis causas:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Não existe matriz curricular ativa para este curso</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>A vigência da matriz não cobre a data da matrícula</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>A matrícula não está vinculada a nenhuma matriz</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>Erro temporário ao carregar os dados</span>
                  </li>
                </ul>
              </div>

              {/* Sugestões de solução */}
              <div className="bg-green-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faGraduationCap} className="text-green-600" />
                  O que fazer:
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Verifique se existe matriz curricular ativa para o curso</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Configure a vigência da matriz para cobrir a data da matrícula</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Execute o script de vinculação de matrizes antigas (se aplicável)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>Entre em contato com o suporte se o problema persistir</span>
                  </li>
                </ul>
              </div>

              {/* Botões de ação */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Voltar
                </button>
                <button
                  onClick={() => navigate('/academico/matriz-curricular')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <FontAwesomeIcon icon={faBook} className="mr-2" />
                  Ver Matrizes Curriculares
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const percentual = progresso.percentualConclusao || 0;

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800 transition-colors"
              title="Voltar"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faChartLine} className="text-blue-600" />
                Progresso do Curso
              </h1>
              <p className="text-gray-600 mt-1">
                Acompanhamento de disciplinas e progresso acadêmico
              </p>
            </div>
            <button
              onClick={handleImprimirPdf}
              disabled={loadingPdf}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 shadow-md ${
                loadingPdf 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
              } text-white`}
              title={loadingPdf ? "Gerando PDF..." : "Imprimir progresso em PDF"}
            >
              <FontAwesomeIcon 
                icon={faPrint} 
                className={loadingPdf ? 'animate-pulse' : ''}
                spin={loadingPdf}
              />
              <span className="hidden sm:inline">
                {loadingPdf ? 'Gerando PDF...' : 'Imprimir PDF'}
              </span>
            </button>
          </div>
        </div>

        {/* Card Compacto - Informações e Progresso */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          {/* Linha 1: Nome e Progresso */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <FontAwesomeIcon icon={faUser} className="text-blue-600 text-xl" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Aluno</div>
                <div className="font-bold text-gray-900">{progresso.nomeAluno}</div>
              </div>
            </div>

            {/* Status do Curso */}
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${
              progresso.statusCurso === 'Concluído' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              <FontAwesomeIcon icon={progresso.statusCurso === 'Concluído' ? faGraduationCap : faBook} />
              {progresso.statusCurso}
            </span>
          </div>

          {/* Linha 2: Cards de Estatísticas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {/* Total de Disciplinas */}
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="bg-purple-100 p-2 rounded-full">
                <FontAwesomeIcon icon={faBook} className="text-purple-600 text-lg" />
              </div>
              <div>
                <div className="text-xs text-gray-600">Total</div>
                <div className="text-xl font-bold text-gray-900">{progresso.totalDisciplinas}</div>
              </div>
            </div>

            {/* Concluídas */}
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <div className="bg-green-100 p-2 rounded-full">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-lg" />
              </div>
              <div>
                <div className="text-xs text-gray-600">Concluídas</div>
                <div className="text-xl font-bold text-gray-900">{progresso.disciplinasConcluidas}</div>
              </div>
            </div>

            {/* Pendentes */}
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
              <div className="bg-red-100 p-2 rounded-full">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 text-lg" />
              </div>
              <div>
                <div className="text-xs text-gray-600">Pendentes</div>
                <div className="text-xl font-bold text-gray-900">{progresso.disciplinasPendentes}</div>
              </div>
            </div>
          </div>

          {/* Barra de Progresso */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progresso Geral
              </span>
              <span className="text-lg font-bold text-blue-600">
                {percentual.toFixed(2)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  percentual === 100 
                    ? 'bg-green-500' 
                    : percentual >= 75 
                    ? 'bg-blue-500' 
                    : percentual >= 50 
                    ? 'bg-yellow-500' 
                    : 'bg-red-500'
                }`}
                style={{ width: `${percentual}%` }}
              />
            </div>
          </div>

          {/* Linha 3: Informações Detalhadas do Aluno */}
          {alunoInfo && (
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Informações do Aluno</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* CPF */}
                {alunoInfo.cpfAluno && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">CPF</div>
                      <div className="text-sm font-medium text-gray-800">{alunoInfo.cpfAluno}</div>
                    </div>
                  </div>
                )}

                {/* RG */}
                {alunoInfo.rgAluno && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">RG</div>
                      <div className="text-sm font-medium text-gray-800">{alunoInfo.rgAluno}</div>
                    </div>
                  </div>
                )}

                {/* Email */}
                {alunoInfo.emailAluno && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">E-mail</div>
                      <div className="text-sm font-medium text-gray-800 break-all">{alunoInfo.emailAluno}</div>
                    </div>
                  </div>
                )}

                {/* Telefone */}
                {alunoInfo.telefoneAluno && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Telefone</div>
                      <div className="text-sm font-medium text-gray-800">{alunoInfo.telefoneAluno}</div>
                    </div>
                  </div>
                )}

                {/* Número de Matrícula */}
                {(alunoInfo.numeroMatricula || alunoInfo.numeroMatriculaPrincipal) && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faHashtag} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Matrícula</div>
                      <div className="text-sm font-medium text-gray-800">
                        {alunoInfo.numeroMatricula || alunoInfo.numeroMatriculaPrincipal}
                      </div>
                    </div>
                  </div>
                )}

                {/* Data de Matrícula */}
                {alunoInfo.dataMatricula && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Data Matrícula</div>
                      <div className="text-sm font-medium text-gray-800">
                        {new Date(alunoInfo.dataMatricula).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Curso */}
                {alunoInfo.nomeCurso && (
                  <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                    <FontAwesomeIcon icon={faGraduationCap} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Curso</div>
                      <div className="text-sm font-medium text-gray-800">{alunoInfo.nomeCurso}</div>
                    </div>
                  </div>
                )}

                {/* Status da Matrícula */}
                {alunoInfo.status && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Status Matrícula</div>
                      <div>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          alunoInfo.status === 'Ativa' ? 'bg-green-100 text-green-800' :
                          alunoInfo.status === 'Trancada' ? 'bg-yellow-100 text-yellow-800' :
                          alunoInfo.status === 'Concluída' || alunoInfo.status === 'Concluida' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {alunoInfo.status}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Extensão */}
                {alunoInfo.nomeExtensao && (
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <FontAwesomeIcon icon={faBook} className="text-gray-400 text-sm" />
                    <div>
                      <div className="text-xs text-gray-500">Extensão</div>
                      <div className="text-sm font-medium text-gray-800">{alunoInfo.nomeExtensao}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Módulos e Disciplinas */}
        <div className="space-y-4">
          {matriz.modulos && matriz.modulos.map((modulo) => (
            <div key={modulo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Header do Módulo */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
                <h3 className="text-lg font-bold">
                  Módulo {modulo.ordem}: {modulo.nome}
                </h3>
                {modulo.descricao && (
                  <p className="text-sm text-blue-100 mt-1">{modulo.descricao}</p>
                )}
              </div>

              {/* Disciplinas */}
              <div className="p-4">
                {modulo.disciplinas && modulo.disciplinas.length > 0 ? (
                  <div className="space-y-2">
                    {modulo.disciplinas.map((disciplina, index) => {
                      // Verificar se a disciplina está concluída usando os detalhes do backend
                      const detalhe = progresso.detalhes?.find(d => 
                        d.idMatrizDisciplina === disciplina.id
                      );
                      const concluida = detalhe?.concluida === 1 || detalhe?.concluida === true;

                      return (
                        <div 
                          key={disciplina.id}
                          className={`border rounded-lg p-3 flex items-center justify-between ${
                            concluida 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-gray-600 font-medium">{index + 1}.</span>
                            <div className="flex-1">
                              <div className={`font-medium ${concluida ? 'text-green-800' : 'text-gray-800'}`}>
                                {disciplina.nomeProduto}
                              </div>
                              {disciplina.cargaHoraria && (
                                <div className="text-sm text-gray-600">
                                  Carga Horária: {disciplina.cargaHoraria}h
                                </div>
                              )}
                              {detalhe?.media != null && (
                                <div className={`text-sm ${concluida ? 'text-green-600' : 'text-red-600'}`}>
                                  Média: {Number(detalhe.media).toFixed(1)}
                                </div>
                              )}
                            </div>

                            {/* Badge Obrigatória/Optativa */}
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              disciplina.obrigatoria 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {disciplina.obrigatoria ? 'Obrigatória' : 'Optativa'}
                            </span>

                            {/* Status Concluído/Pendente */}
                            {concluida ? (
                              <div className="flex items-center gap-2 text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-lg" />
                                <span className="font-semibold text-sm">Concluída</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-red-600">
                                <FontAwesomeIcon icon={faTimesCircle} className="text-lg" />
                                <span className="font-semibold text-sm">Pendente</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Nenhuma disciplina neste módulo
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mensagem de Conclusão */}
        {progresso.statusCurso === 'Concluído' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6 text-center">
            <FontAwesomeIcon icon={faGraduationCap} className="text-green-600 text-4xl mb-3" />
            <h3 className="text-xl font-bold text-green-800 mb-2">
              Parabéns!
            </h3>
            <p className="text-green-700">
              Você concluiu todas as disciplinas obrigatórias deste curso!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlunoProgressoView;
